import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import {
  Button,
  Card,
  Content as CardContent,
  Text as CardDescription,
  Footer as CardFooter,
  Header as CardHeader,
  TextField as Input,
} from '@react-spectrum/s2'
import { useState } from 'react'
import { z } from 'zod'
import { getAuthSession } from '@rpc/auth-session.rpc'
import { authClient } from '@client/auth-client'
import { trackAnalyticsEvent } from '@client/analytics'
import { APP_ROUTES } from '@shared/app-routes'
import { resolveSafeRedirectPath } from '@shared/safe-redirect'
import type { AuthMethod } from '@shared/auth-analytics'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import {
  flexGrowButtonStyles,
  fullWidthButtonStyles,
  fullWidthStyles,
} from '@/styles/s2-macros'

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: (search) => loginSearchSchema.parse(search),
  beforeLoad: async ({ search }) => {
    const session = await getAuthSession()
    if (session?.user) {
      throw redirect({
        to: resolveSafeRedirectPath(search.redirect),
      })
    }
  },
  component: Login,
  head: () => ({
    meta: [{ title: 'Login - Building regs assistant' }],
  }),
})

function Login() {
  const { redirect: redirectTo } = Route.useSearch()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const [sentToEmail, setSentToEmail] = useState('')

  const buildPostSigninCallback = (method: AuthMethod) => {
    const safeRedirect = resolveSafeRedirectPath(redirectTo)
    const params = new URLSearchParams({
      redirect: safeRedirect,
      method,
    })
    return `${APP_ROUTES.auth.postSignin}?${params.toString()}`
  }

  const handleSendMagicLink = async () => {
    setError(null)
    setLoading(true)
    trackAnalyticsEvent('login_method_selected', {
      method: 'magic_link',
      has_redirect: Boolean(redirectTo),
    })

    try {
      await authClient.signIn.magicLink(
        {
          email,
          callbackURL: buildPostSigninCallback('magic_link'),
        },
        {
          onRequest: () => {
            setLoading(true)
          },
          onSuccess: () => {
            setLoading(false)
            setEmailSent(true)
            setSentToEmail(email)
            trackAnalyticsEvent('login_magic_link_sent', {
              has_redirect: Boolean(redirectTo),
            })
          },
          onError: (ctx) => {
            setLoading(false)
            setError(
              ctx.error.message ||
                'Failed to send login link. Please try again.',
            )
            trackAnalyticsEvent('login_failed', {
              method: 'magic_link',
              stage: 'send_link',
            })
          },
        },
      )
    } catch (e: any) {
      setLoading(false)
      setError(e.message || 'Failed to send login link. Please try again.')
      trackAnalyticsEvent('login_failed', {
        method: 'magic_link',
        stage: 'send_link',
      })
    }
  }

  const handleGoogleSignIn = async () => {
    setError(null)
    trackAnalyticsEvent('login_method_selected', {
      method: 'google',
      has_redirect: Boolean(redirectTo),
    })
    try {
      await authClient.signIn.social({
        provider: 'google',
        callbackURL: buildPostSigninCallback('google'),
      })
    } catch (e: any) {
      setError(e.message || 'Google sign-in failed. Please try again.')
      trackAnalyticsEvent('login_failed', {
        method: 'google',
        stage: 'social_redirect',
      })
    }
  }

  const handleAppleSignIn = async () => {
    setError(null)
    trackAnalyticsEvent('login_method_selected', {
      method: 'apple',
      has_redirect: Boolean(redirectTo),
    })
    try {
      await authClient.signIn.social({
        provider: 'apple',
        callbackURL: buildPostSigninCallback('apple'),
      })
    } catch (e: any) {
      setError(e.message || 'Apple sign-in failed. Please try again.')
      trackAnalyticsEvent('login_failed', {
        method: 'apple',
        stage: 'social_redirect',
      })
    }
  }

  const handleMicrosoftSignIn = async () => {
    setError(null)
    trackAnalyticsEvent('login_method_selected', {
      method: 'microsoft',
      has_redirect: Boolean(redirectTo),
    })
    try {
      await authClient.signIn.social({
        provider: 'microsoft',
        callbackURL: buildPostSigninCallback('microsoft'),
      })
    } catch (e: any) {
      setError(e.message || 'Microsoft sign-in failed. Please try again.')
      trackAnalyticsEvent('login_failed', {
        method: 'microsoft',
        stage: 'social_redirect',
      })
    }
  }

  const handleTryDifferentEmail = () => {
    trackAnalyticsEvent('login_email_change_clicked', {
      has_redirect: Boolean(redirectTo),
    })
    setEmailSent(false)
    setSentToEmail('')
    setError(null)
  }

  const handleResend = () => {
    trackAnalyticsEvent('login_magic_link_resend_clicked', {
      has_redirect: Boolean(redirectTo),
    })
    setEmailSent(false)
    handleSendMagicLink()
  }

  const isValidEmail =
    email.length > 0 && email.includes('@') && email.includes('.')
  const showMicrosoftOption =
    import.meta.env.VITE_ENABLE_MICROSOFT_SIGN_IN === 'true'

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main
        id="main-content"
        className="flex flex-1 items-center justify-center p-4"
      >
        <div className="w-full max-w-md">
          <Card styles={fullWidthStyles} data-testid="login-card">
            <CardHeader>
              <div className="pb-4 text-center">
                <h1 className="text-2xl font-bold">Welcome</h1>
                <CardDescription>
                  {emailSent
                    ? 'Check your email for a sign-in link'
                    : 'Sign in or create an account'}
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent>
              <div className="space-y-4">
                {!emailSent ? (
                  <>
                    {/* Google Sign-In Button */}
                    <Button
                      onPress={handleGoogleSignIn}
                      variant="secondary"
                      fillStyle="outline"
                      styles={fullWidthButtonStyles}
                      size="L"
                      isDisabled={loading}
                    >
                      <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap">
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        <span>Continue with Google</span>
                      </span>
                    </Button>

                    {/* Apple Sign-In Button */}
                    <Button
                      onPress={handleAppleSignIn}
                      variant="secondary"
                      fillStyle="outline"
                      styles={fullWidthButtonStyles}
                      size="L"
                      isDisabled={loading}
                    >
                      <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap">
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 384 512"
                          fill="currentColor"
                        >
                          <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 69 126.7 112.5 126.7 16.5 0 41.4-15.4 75.1-15.4 32.5 0 52.2 15.4 76.4 15.4 31.6 0 78.6-103.7 90.3-130.8-15.9-6.6-63.4-27.1-64-82.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z" />
                        </svg>
                        <span>Continue with Apple</span>
                      </span>
                    </Button>

                    {showMicrosoftOption && (
                      <Button
                        onPress={handleMicrosoftSignIn}
                        variant="secondary"
                        fillStyle="outline"
                        styles={fullWidthButtonStyles}
                        size="L"
                        isDisabled={loading}
                      >
                        <span className="inline-flex items-center justify-center gap-2 whitespace-nowrap">
                          <svg
                            className="h-5 w-5"
                            viewBox="0 0 23 23"
                            fill="none"
                          >
                            <path d="M11 0H0V11H11V0Z" fill="#F25022" />
                            <path d="M23 0H12V11H23V0Z" fill="#7FBA00" />
                            <path d="M11 12H0V23H11V12Z" fill="#00A4EF" />
                            <path d="M23 12H12V23H23V12Z" fill="#FFB900" />
                          </svg>
                          <span>Continue with Microsoft</span>
                        </span>
                      </Button>
                    )}

                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs font-medium">
                        <span className="bg-card px-2 text-muted-foreground">
                          or
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="login-email">Email</label>
                      <Input
                        id="login-email"
                        type="email"
                        name="username"
                        autoComplete="username"
                        inputMode="email"
                        autoCapitalize="none"
                        aria-label="Email address"
                        spellCheck={false}
                        placeholder="you@example.com"
                        value={email}
                        onChange={setEmail}
                        size="L"
                        isDisabled={loading}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && isValidEmail) {
                            handleSendMagicLink()
                          }
                        }}
                      />
                    </div>

                    {error && (
                      <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md border border-red-200 dark:border-red-800 flex items-center gap-2">
                        <span className="font-medium">Error:</span> {error}
                      </div>
                    )}

                    <Button
                      onPress={handleSendMagicLink}
                      styles={fullWidthButtonStyles}
                      size="L"
                      isDisabled={loading || !isValidEmail}
                    >
                      {loading ? 'Sending…' : 'Send login link'}
                    </Button>

                    <div className="pt-2 text-center">
                      <p className="text-xs text-muted-foreground">
                        We'll send you a link to sign in. No password needed.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-4 py-6 text-center">
                      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
                        <svg
                          className="h-8 w-8 text-muted-foreground"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Email sent to
                        </p>
                        <p className="font-semibold text-foreground">
                          {sentToEmail}
                        </p>
                      </div>

                      <p className="text-sm text-muted-foreground">
                        Click the link in the email to sign in. The link expires
                        in 5 minutes.
                      </p>
                    </div>

                    {error && (
                      <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md border border-red-200 dark:border-red-800 flex items-center gap-2">
                        <span className="font-medium">Error:</span> {error}
                      </div>
                    )}

                    <div className="flex gap-3 pt-2">
                      <Button
                        variant="secondary"
                        fillStyle="outline"
                        onPress={handleTryDifferentEmail}
                        styles={flexGrowButtonStyles}
                        size="L"
                        isDisabled={loading}
                      >
                        Change email
                      </Button>
                      <Button
                        onPress={handleResend}
                        styles={flexGrowButtonStyles}
                        size="L"
                        isDisabled={loading}
                      >
                        Resend
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </CardContent>

            <CardFooter>
              <div className="flex justify-center pb-6">
                <p className="text-center text-xs text-muted-foreground">
                  By continuing, you agree to our{' '}
                  <Link
                    to="/terms"
                    onClick={() =>
                      trackAnalyticsEvent('navigation_clicked', {
                        destination: '/terms',
                        label: 'terms',
                        location: 'login',
                      })
                    }
                    className="underline hover:text-foreground"
                  >
                    Terms of service
                  </Link>{' '}
                  and{' '}
                  <Link
                    to="/privacy"
                    onClick={() =>
                      trackAnalyticsEvent('navigation_clicked', {
                        destination: '/privacy',
                        label: 'privacy',
                        location: 'login',
                      })
                    }
                    className="underline hover:text-foreground"
                  >
                    Privacy policy
                  </Link>
                  .
                </p>
              </div>
            </CardFooter>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  )
}
